import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Icon,
  SectionTitle,
  AnalysisMetricsGrid,
  AnalysisMetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
} from '../../../atoms'
import {
  TransportStopsTable,
  StopRow,
  StopIcon,
  StopName,
  StopDistance,
} from '../../../molecules/TransportStopsTable'
import { useTransportAndAccess } from '../TransportAndAccessContext'

export const TransportAndAccessEVCharging: React.FC = () => {
  const { t } = useTranslation('transport')
  const { activeMobilityMetrics } = useTransportAndAccess()

  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} ${t('units.kilometers', { ns: 'transport' })}`
    }
    return `${Math.round(meters)} ${t('units.meters', { ns: 'transport' })}`
  }

  const evStations = activeMobilityMetrics?.evStationsNearby || []

  // Group chargers by station address
  const groupChargersByStation = (chargers: any[]) => {
    const stationMap = new Map<string, any>()

    chargers.forEach((charger) => {
      const address = charger.attributes.address

      if (!stationMap.has(address)) {
        // Initialize station entry
        stationMap.set(address, {
          address,
          chargers: [],
          distance: charger.distance,
          operator: charger.attributes.operator,
          accessibility: charger.attributes.accessibility,
          isOpen24Hours: charger.attributes.isOpen24Hours,
          phoneNumber: charger.attributes.phoneNumber,
          geometry: charger.geometry,
        })
      }

      // Add charger to station
      stationMap.get(address).chargers.push({
        name: charger.attributes.name,
        power: charger.attributes.power,
        status: charger.attributes.status,
        plugTypes: Array.isArray(charger.attributes.plugTypes)
          ? charger.attributes.plugTypes
          : [charger.attributes.plugTypes],
      })
    })

    // Convert to array and aggregate data
    return Array.from(stationMap.values())
      .map((station) => {
        const chargers = station.chargers

        // Get unique plug types
        const allPlugTypes = chargers
          .flatMap((c: any) => c.plugTypes)
          .filter(Boolean)
        const uniquePlugTypes = [...new Set(allPlugTypes)]

        // Get power range
        const powers = chargers
          .map((c: any) => parseFloat(c.power))
          .filter((p: number) => !isNaN(p))
        const minPower = powers.length > 0 ? Math.min(...powers) : 0
        const maxPower = powers.length > 0 ? Math.max(...powers) : 0

        // Get status counts
        const statusCounts = chargers.reduce((acc: any, c: any) => {
          acc[c.status] = (acc[c.status] || 0) + 1
          return acc
        }, {})

        return {
          ...station,
          chargerCount: chargers.length,
          plugTypes: uniquePlugTypes,
          minPower,
          maxPower,
          statusCounts,
        }
      })
      .sort((a, b) => a.distance - b.distance)
  }

  const groupedStations = groupChargersByStation(evStations)

  // Get charging station type based on max power
  const getStationType = (
    maxPower: number
  ): { type: 'bus' | 'tram' | 'train'; icon: string; label: string } => {
    if (maxPower >= 50)
      return {
        type: 'train',
        icon: 'zap',
        label: t('evCharging.chargingTypes.fast'),
      }
    if (maxPower >= 22)
      return {
        type: 'tram',
        icon: 'plug',
        label: t('evCharging.chargingTypes.medium'),
      }
    return {
      type: 'bus',
      icon: 'battery-charging',
      label: t('evCharging.chargingTypes.standard'),
    }
  }

  // Translate plug type from API to localized string
  const translatePlugType = (plugType: string): string => {
    if (!plugType) return t('evCharging.plugTypes.unknown')

    const normalized = plugType.toLowerCase().trim()

    if (normalized.includes('type 2') && normalized.includes('connector'))
      return t('evCharging.plugTypes.type2Connector')
    if (normalized.includes('type 2') && normalized.includes('outlet'))
      return t('evCharging.plugTypes.type2Outlet')
    if (normalized.includes('type 2'))
      return t('evCharging.plugTypes.type2Outlet')
    if (normalized.includes('type 1') && normalized.includes('connector'))
      return t('evCharging.plugTypes.type1Connector')
    if (normalized.includes('type 1') && normalized.includes('outlet'))
      return t('evCharging.plugTypes.type1Outlet')
    if (normalized.includes('type 1'))
      return t('evCharging.plugTypes.type1Outlet')
    if (normalized.includes('ccs') || normalized.includes('combo 2'))
      return t('evCharging.plugTypes.ccsCombo2')
    if (normalized.includes('chademo')) return t('evCharging.plugTypes.chademo')
    if (normalized.includes('schuko')) return t('evCharging.plugTypes.schuko')

    // Return original if no match found
    return plugType
  }

  // Summary metrics based on grouped stations
  const totalStations = groupedStations.length
  const fastChargingStations = groupedStations.filter(
    (s) => s.maxPower >= 50
  ).length
  const freeStations = groupedStations.filter((s) =>
    s.accessibility?.toLowerCase().includes('free')
  ).length

  const summaryMetrics = [
    {
      title: t('evCharging.metrics.nearestStation.title'),
      value: activeMobilityMetrics?.nearestEVStation
        ? formatDistance(activeMobilityMetrics.nearestEVStation.distance)
        : t('units.na', { ns: 'transport' }),
      icon: 'map-pin',
      color: '#22c55e',
    },
    {
      title: t('evCharging.metrics.totalStations5km.title'),
      value: totalStations,
      icon: 'plug',
      color: '#3b82f6',
    },
    {
      title: t('evCharging.metrics.fastCharging.title'),
      value: fastChargingStations,
      icon: 'zap',
      color: '#f59e0b',
    },
    {
      title: t('evCharging.metrics.freeAccess.title'),
      value: freeStations,
      icon: 'check-circle',
      color: '#10b981',
    },
  ]

  return (
    <>
      <SectionTitle>{t('evCharging.overview')}</SectionTitle>
      <AnalysisMetricsGrid>
        {summaryMetrics.map((metric) => (
          <AnalysisMetricCard key={metric.title}>
            <MetricHeader>
              <MetricIcon color={metric.color}>
                <Icon name={metric.icon as any} size="small" />
              </MetricIcon>
              <MetricTitle>{metric.title}</MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              <MetricValue>{metric.value}</MetricValue>
            </Box>
          </AnalysisMetricCard>
        ))}
      </AnalysisMetricsGrid>

      <Box mt="32px">
        <SectionTitle>{t('evCharging.nearbyStations')}</SectionTitle>
        <TransportStopsTable>
          {groupedStations.map((station, index) => {
            const stationType = getStationType(station.maxPower)
            const powerRange =
              station.minPower === station.maxPower
                ? `${station.maxPower} ${t('units.kw', { ns: 'transport' })}`
                : `${station.minPower}-${station.maxPower} ${t('units.kw', { ns: 'transport' })}`

            return (
              <StopRow key={`${station.address}-${index}`}>
                <StopIcon type={stationType.type}>
                  <Icon name={stationType.icon as any} size="small" />
                </StopIcon>
                <StopName>
                  {station.address}
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginTop: '4px',
                    }}
                  >
                    {station.chargerCount}{' '}
                    {station.chargerCount > 1
                      ? t('evCharging.charger.plural')
                      : t('evCharging.charger.singular')}{' '}
                    • {powerRange} •{' '}
                    {station.operator && station.operator !== 'Unknown'
                      ? station.operator
                      : t('evCharging.unknownOperator')}
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      marginTop: '2px',
                    }}
                  >
                    {station.plugTypes
                      .slice(0, 2)
                      .map((pt: string) => translatePlugType(pt))
                      .join(', ')}
                    {station.plugTypes.length > 2 &&
                      ` ${t('evCharging.more', { count: station.plugTypes.length - 2 })}`}
                  </div>
                </StopName>
                <StopDistance>{formatDistance(station.distance)}</StopDistance>
              </StopRow>
            )
          })}
        </TransportStopsTable>
      </Box>
    </>
  )
}
